import { useCallback, useEffect, useRef, useState } from "react";
import {
  getAvailableIndexes,
  listDocuments,
  uploadDocuments,
  deleteDocument,
  reindexDocument,
  startIngestion,
  getBatchStatus,
  checkUploadDuplicates,
  SearchIndexInfo,
  UploadedDocument,
  BatchStatusDocument,
  UploadConflict,
} from "../../api/adminDocuments";
import {
  ArrowUpload20Regular,
  DocumentArrowUp20Regular,
  ArrowSync20Regular,
  Delete20Regular,
  Dismiss12Regular,
  Dismiss20Regular,
  DocumentMultiple20Regular,
  ChevronLeft20Regular,
  ChevronRight20Regular,
  Info20Regular,
  Checkmark20Regular,
  ErrorCircle20Regular,
  Search20Regular,
  Filter20Regular,
} from "@fluentui/react-icons";
import { ConfirmDialog } from "../../shell/components/ConfirmDialog/ConfirmDialog";
import styles from "./DocumentUploadsTab.module.css";

const STATUS_LABELS: Record<number | string, string> = {
  0: "Uploaded",
  1: "Extracting",
  2: "Chunking",
  3: "Embedding",
  4: "Indexing",
  5: "Indexed",
  6: "Failed",
  7: "Deleted",
  8: "Extracting Images",
  9: "Processing Images",
  Uploaded: "Uploaded",
  Extracting: "Extracting",
  Chunking: "Chunking",
  Embedding: "Embedding",
  Indexing: "Indexing",
  Indexed: "Indexed",
  Failed: "Failed",
  Deleted: "Deleted",
  ExtractingImages: "Extracting Images",
  ProcessingImages: "Processing Images",
};

function getStatusClass(status: number | string): string {
  const s = String(status);
  if (s === "5" || s === "Indexed") return styles.statusIndexed;
  if (s === "6" || s === "Failed") return styles.statusFailed;
  if (s === "0" || s === "Uploaded") return styles.statusUploaded;
  return styles.statusProcessing;
}

export function DocumentUploadsTab() {
  const [documents, setDocuments] = useState<UploadedDocument[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [indexes, setIndexes] = useState<SearchIndexInfo[]>([]);
  const [selectedIndex, setSelectedIndex] = useState("");
  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
  const [category, setCategory] = useState("");
  const [description, setDescription] = useState("");
  const [uploading, setUploading] = useState(false);
  const [uploadStage, setUploadStage] = useState<string | null>(null);
  const [batchProgress, setBatchProgress] = useState<BatchStatusDocument[] | null>(null);
  const [uploadResult, setUploadResult] = useState<{
    success: boolean;
    documents: { originalFileName: string; status: string; statusMessage?: string }[];
  } | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [dragOver, setDragOver] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);

  // Search & filter state
  const [searchQuery, setSearchQuery] = useState("");
  const [filterStatus, setFilterStatus] = useState<string>("");
  const [filterIndex, setFilterIndex] = useState<string>("");
  const searchTimeout = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [deleteConfirm, setDeleteConfirm] = useState<{ id: string; fileName: string } | null>(null);
  const [duplicateWarning, setDuplicateWarning] = useState<UploadConflict[] | null>(null);
  const [reindexingIds, setReindexingIds] = useState<Set<string>>(new Set());

  const PAGE_SIZE = 20;

  const loadDocuments = useCallback(async () => {
    try {
      const statusNum = filterStatus ? parseInt(filterStatus, 10) : undefined;
      const result = await listDocuments(
        page,
        PAGE_SIZE,
        statusNum,
        filterIndex || undefined,
        searchQuery || undefined
      );
      const activeDocs = result.documents.filter(
        (d) => String(d.status) !== "7" && String(d.status) !== "Deleted"
      );
      setDocuments(activeDocs);
      setTotal(result.total);
    } catch {
      setError("Failed to load documents");
    }
  }, [page, searchQuery, filterStatus, filterIndex]);

  useEffect(() => {
    getAvailableIndexes()
      .then((idx) => {
        setIndexes(idx);
      })
      .catch((e) => {
        setError(`Failed to load indexes: ${e instanceof Error ? e.message : "Unknown error"}`);
      });
  }, []);

  useEffect(() => {
    loadDocuments();
  }, [loadDocuments]);

  const handleFilesSelected = (files: FileList | null) => {
    if (!files) return;
    setSelectedFiles((prev) => [...prev, ...Array.from(files)]);
  };

  const removeFile = (index: number) => {
    setSelectedFiles((prev) => prev.filter((_, i) => i !== index));
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setDragOver(false);
    handleFilesSelected(e.dataTransfer.files);
  };

  const handleUpload = async () => {
    if (selectedFiles.length === 0 || !selectedIndex) return;

    // Check for duplicates first
    try {
      const fileNames = selectedFiles.map((f) => f.name);
      const conflicts = await checkUploadDuplicates(fileNames, selectedIndex);
      if (conflicts.length > 0 && !duplicateWarning) {
        setDuplicateWarning(conflicts);
        return;
      }
    } catch {
      // If duplicate check fails, proceed with upload anyway
    }

    setDuplicateWarning(null);
    setUploading(true);
    setError(null);
    setUploadResult(null);
    setBatchProgress(null);
    setUploadStage("Uploading files to storage...");

    try {
      const result = await uploadDocuments(
        selectedFiles,
        selectedIndex,
        category || undefined,
        description || undefined
      );

      const batchId = result.batchId;
      setUploadStage("Starting document ingestion...");

      // Start the Durable Functions orchestrator
      await startIngestion(batchId);

      setUploadStage("Processing documents...");
      setSelectedFiles([]);
      setCategory("");
      setDescription("");

      // Poll for batch status
      pollRef.current = setInterval(async () => {
        try {
          const status = await getBatchStatus(batchId);
          setBatchProgress(status.documents);

          if (status.overallStatus === "Completed" || status.overallStatus === "PartialFailure") {
            if (pollRef.current) clearInterval(pollRef.current);
            pollRef.current = null;

            const allIndexed = status.overallStatus === "Completed";
            setUploadResult({
              success: allIndexed,
              documents: status.documents.map((d) => ({
                originalFileName: d.fileName,
                status: d.status,
                statusMessage: d.statusMessage ?? undefined,
              })),
            });
            setUploadStage(null);
            setBatchProgress(null);
            setUploading(false);
            loadDocuments();
          }
        } catch {
          // Ignore transient poll errors
        }
      }, 3000);
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : "Upload failed");
      setUploadStage(null);
      setUploading(false);
    }
  };

  // Cleanup polling on unmount
  useEffect(() => {
    return () => {
      if (pollRef.current) clearInterval(pollRef.current);
    };
  }, []);

  const handleDelete = async (id: string) => {
    try {
      await deleteDocument(id);
      loadDocuments();
    } catch {
      setError("Failed to delete document");
    } finally {
      setDeleteConfirm(null);
    }
  };

  const handleReindex = async (id: string) => {
    setReindexingIds((prev) => new Set(prev).add(id));
    try {
      await reindexDocument(id);
      loadDocuments();
    } catch {
      setError("Failed to reindex document");
    } finally {
      setReindexingIds((prev) => {
        const next = new Set(prev);
        next.delete(id);
        return next;
      });
    }
  };

  const handleSearchChange = (value: string) => {
    setSearchQuery(value);
    setPage(1);
  };

  const handleDebouncedSearch = (value: string) => {
    if (searchTimeout.current) clearTimeout(searchTimeout.current);
    searchTimeout.current = setTimeout(() => handleSearchChange(value), 300);
  };

  const totalPages = Math.ceil(total / PAGE_SIZE);

  return (
    <div className={styles.container}>
      {/* Description banner */}
      <div className={styles.description}>
        <span className={styles.descIcon}><Info20Regular /></span>
        Upload documents to a product knowledge base. Select the target index to specify which
        product the document belongs to, then upload files for automatic extraction, chunking,
        and indexing.
      </div>

      {/* Error */}
      {error && (
        <div className={styles.error}>
          {error}
          <button className={styles.errorDismiss} onClick={() => setError(null)}>
            <Dismiss20Regular />
          </button>
        </div>
      )}

      {/* Upload progress */}
      {uploadStage && (
        <div className={styles.ingestionBanner}>
          <span className={styles.ingestionSpinner} />
          {uploadStage}
        </div>
      )}

      {/* Per-document progress */}
      {batchProgress && batchProgress.length > 0 && (
        <div className={styles.batchProgress}>
          {batchProgress.map((doc) => (
            <div key={doc.id} className={styles.batchProgressItem}>
              <span className={styles.batchProgressFile}>{doc.fileName}</span>
              <span className={`${styles.statusBadge} ${getStatusClass(doc.status)}`}>
                {STATUS_LABELS[doc.status] ?? doc.status}
              </span>
            </div>
          ))}
        </div>
      )}

      {/* Upload result */}
      {uploadResult && (
        <div className={uploadResult.success ? styles.successBanner : styles.warningBanner}>
          <span className={styles.resultIcon}>
            {uploadResult.success ? <Checkmark20Regular /> : <ErrorCircle20Regular />}
          </span>
          <div className={styles.resultContent}>
            <strong>
              {uploadResult.success
                ? `All ${uploadResult.documents.length} document(s) uploaded and indexed successfully!`
                : "Upload completed with issues:"}
            </strong>
            {!uploadResult.success && (
              <ul className={styles.resultList}>
                {uploadResult.documents.map((d, i) => (
                  <li key={i} className={d.status === "Failed" ? styles.resultFailed : ""}>
                    {d.originalFileName} — {STATUS_LABELS[d.status] || d.status}
                    {d.statusMessage && ` (${d.statusMessage})`}
                  </li>
                ))}
              </ul>
            )}
          </div>
          <button
            className={styles.errorDismiss}
            onClick={() => setUploadResult(null)}
          >
            <Dismiss20Regular />
          </button>
        </div>
      )}

      {/* Two-column layout */}
      <div className={styles.twoColumnLayout}>
        {/* Left: Upload card */}
        <div className={styles.uploadColumn}>
          <div className={styles.uploadCard}>
            <h3 className={styles.uploadCardTitle}>
              <ArrowUpload20Regular />
              Upload Documents
            </h3>

            <div className={styles.formGrid}>
              <div className={styles.formField}>
                <label className={styles.formLabel}>Target Product Index</label>
                <select
                  className={styles.formSelect}
                  value={selectedIndex}
                  onChange={(e) => setSelectedIndex(e.target.value)}
                >
                  <option value="">
                    {indexes.length === 0 ? "Loading indexes..." : "— Select a product index —"}
                  </option>
                  {indexes.map((idx) => (
                    <option key={idx.id} value={idx.id}>
                      {idx.displayName}
                    </option>
                  ))}
                </select>
              </div>
              <div className={styles.formField}>
                <label className={styles.formLabel}>Category (optional)</label>
                <input
                  className={styles.formInput}
                  type="text"
                  value={category}
                  onChange={(e) => setCategory(e.target.value)}
                  placeholder="e.g. API Reference, User Guide"
                />
              </div>
              <div className={styles.formFieldFull}>
                <label className={styles.formLabel}>Description (optional)</label>
                <input
                  className={styles.formInput}
                  type="text"
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  placeholder="Brief description of the uploaded documents"
                />
              </div>
            </div>

            {/* Drop zone */}
            <div
              className={`${styles.fileZone} ${dragOver ? styles.fileZoneDragOver : ""}`}
              onClick={() => fileInputRef.current?.click()}
              onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
              onDragLeave={() => setDragOver(false)}
              onDrop={handleDrop}
            >
              <div className={styles.fileZoneIcon}>
                <DocumentArrowUp20Regular />
              </div>
              <div className={styles.fileZoneText}>
                <strong>Click to browse</strong> or drag and drop files here
              </div>
              <div className={styles.fileZoneHint}>
                Supported: PDF, DOCX, PPTX, XLSX, TXT, MD, CSV — up to 50 MB each
              </div>
            </div>
            <input
              ref={fileInputRef}
              type="file"
              multiple
              accept=".pdf,.docx,.pptx,.xlsx,.txt,.md,.csv"
              style={{ display: "none" }}
              onChange={(e) => handleFilesSelected(e.target.files)}
            />

            {/* Selected files chips */}
            {selectedFiles.length > 0 && (
              <div className={styles.selectedFiles}>
                {selectedFiles.map((f, i) => (
                  <span key={i} className={styles.fileChip}>
                    {f.name}
                    <button className={styles.fileChipRemove} onClick={() => removeFile(i)}>
                      <Dismiss12Regular />
                    </button>
                  </span>
                ))}
              </div>
            )}

            {/* Duplicate warning */}
            {duplicateWarning && duplicateWarning.length > 0 && (
              <div className={styles.warningBanner}>
                <span className={styles.resultIcon}>
                  <ErrorCircle20Regular />
                </span>
                <div className={styles.resultContent}>
                  <strong>The following file(s) already exist in this index:</strong>
                  <ul className={styles.resultList}>
                    {duplicateWarning.map((c, i) => (
                      <li key={i}>
                        {c.originalFileName} (uploaded {new Date(c.uploadedAt).toLocaleDateString()})
                      </li>
                    ))}
                  </ul>
                  <div className={styles.duplicateActions}>
                    <button className={styles.uploadBtn} onClick={handleUpload}>
                      Upload Anyway (Replace)
                    </button>
                    <button
                      className={styles.duplicateCancelBtn}
                      onClick={() => setDuplicateWarning(null)}
                    >
                      Cancel
                    </button>
                  </div>
                </div>
                <button
                  className={styles.errorDismiss}
                  onClick={() => setDuplicateWarning(null)}
                >
                  <Dismiss20Regular />
                </button>
              </div>
            )}

            {/* Upload button */}
            <div className={styles.uploadActions}>
              <button
                className={styles.uploadBtn}
                onClick={handleUpload}
                disabled={uploading || selectedFiles.length === 0 || !selectedIndex}
              >
                <ArrowUpload20Regular />
                {uploading ? "Uploading..." : `Upload & Process${selectedFiles.length > 0 ? ` (${selectedFiles.length})` : ""}`}
              </button>
            </div>
          </div>
        </div>

        {/* Right: Documents list */}
        <div className={styles.documentsColumn}>
          <div className={styles.documentsPanel}>
            <div className={styles.sectionHeader}>
              <span className={styles.sectionTitle}>Uploaded Documents</span>
              <span className={styles.docCount}>{total} document{total !== 1 ? "s" : ""}</span>
            </div>

            {/* Search and filters */}
            <div className={styles.searchFilterBar}>
              <div className={styles.searchBox}>
                <Search20Regular className={styles.searchIcon} />
                <input
                  className={styles.searchInput}
                  type="text"
                  placeholder="Search by file name..."
                  onChange={(e) => handleDebouncedSearch(e.target.value)}
                />
              </div>
              <div className={styles.filterGroup}>
                <Filter20Regular className={styles.filterIcon} />
                <select
                  className={styles.filterSelect}
                  value={filterStatus}
                  onChange={(e) => { setFilterStatus(e.target.value); setPage(1); }}
                >
                  <option value="">All statuses</option>
                  <option value="0">Uploaded</option>
                  <option value="5">Indexed</option>
                  <option value="6">Failed</option>
                </select>
                <select
                  className={styles.filterSelect}
                  value={filterIndex}
                  onChange={(e) => { setFilterIndex(e.target.value); setPage(1); }}
                >
                  <option value="">All indexes</option>
                  {indexes.map((idx) => (
                    <option key={idx.id} value={idx.id}>
                      {idx.displayName}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            {/* Document rows */}
            {documents.length === 0 ? (
              <div className={styles.emptyState}>
                <div className={styles.emptyStateIcon}>
                  <DocumentMultiple20Regular />
                </div>
                <div className={styles.emptyStateText}>
                  {searchQuery || filterStatus || filterIndex
                    ? "No documents match your filters."
                    : "No documents uploaded yet."}
                </div>
              </div>
            ) : (
              <div className={styles.docList}>
                {documents.map((doc) => (
                  <div key={doc.uploadedDocumentId} className={styles.docCard}>
                    <span className={styles.docFileName} title={doc.originalFileName}>
                      {doc.originalFileName}
                    </span>
                    <span className={`${styles.statusBadge} ${getStatusClass(doc.status)}`}>
                      {STATUS_LABELS[doc.status] ?? "Unknown"}
                    </span>
                    <span className={styles.docDate}>
                      {new Date(doc.uploadedAt).toLocaleDateString()}
                    </span>
                    <span className={styles.docActions}>
                      <button
                        className={`${styles.actionBtn} ${reindexingIds.has(doc.uploadedDocumentId) ? styles.actionBtnSpinning : ""}`}
                        onClick={() => handleReindex(doc.uploadedDocumentId)}
                        title="Reindex document"
                        disabled={reindexingIds.has(doc.uploadedDocumentId)}
                      >
                        <ArrowSync20Regular />
                      </button>
                      <button
                        className={`${styles.actionBtn} ${styles.actionBtnDanger}`}
                        onClick={() => setDeleteConfirm({ id: doc.uploadedDocumentId, fileName: doc.originalFileName })}
                        title="Delete document"
                      >
                        <Delete20Regular />
                      </button>
                    </span>
                  </div>
                ))}
              </div>
            )}

            {/* Pagination */}
            {totalPages > 1 && (
              <div className={styles.pagination}>
                <button
                  className={styles.paginationBtn}
                  disabled={page <= 1}
                  onClick={() => setPage(page - 1)}
                >
                  <ChevronLeft20Regular />
                </button>
                <span className={styles.paginationText}>
                  Page {page} of {totalPages}
                </span>
                <button
                  className={styles.paginationBtn}
                  disabled={page >= totalPages}
                  onClick={() => setPage(page + 1)}
                >
                  <ChevronRight20Regular />
                </button>
              </div>
            )}
          </div>
        </div>
      </div>

      <ConfirmDialog
        isOpen={deleteConfirm !== null}
        title="Delete Document"
        message={`Are you sure you want to delete "${deleteConfirm?.fileName ?? ""}"? This will permanently remove the document and its data from the search index.`}
        confirmLabel="Delete"
        cancelLabel="Cancel"
        variant="danger"
        onConfirm={() => deleteConfirm && handleDelete(deleteConfirm.id)}
        onCancel={() => setDeleteConfirm(null)}
      />
    </div>
  );
}
